import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  RefreshCw,
  Wifi,
  WifiOff,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

// Platform icons and colors
const PLATFORM_CONFIG = {
  facebook: { name: 'Facebook', color: '#1877F2', icon: '📘' },
  instagram: { name: 'Instagram', color: '#E4405F', icon: '📷' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2', icon: '💼' },
  twitter: { name: 'X (Twitter)', color: '#000000', icon: '𝕏' },
  tiktok: { name: 'TikTok', color: '#000000', icon: '🎵' },
  pinterest: { name: 'Pinterest', color: '#E60023', icon: '📌' },
  youtube: { name: 'YouTube', color: '#FF0000', icon: '▶️' },
  google_business: { name: 'Google Business', color: '#4285F4', icon: '🏢' },
  snapchat: { name: 'Snapchat', color: '#FFFC00', icon: '👻' },
  threads: { name: 'Threads', color: '#000000', icon: '🧵' },
  bluesky: { name: 'Bluesky', color: '#0085FF', icon: '🦋' }
};

const AnalyticsHealthWidget = ({ compact = false }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealthData = async () => {
    try {
      const response = await api.get('/analytics/health');
      setHealthData(response.data);
    } catch (error) {
      console.error('Error fetching analytics health:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchHealthData, 300000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/analytics/refresh');
      toast.success('Analytics refresh started');
      // Wait a bit then refetch health data
      setTimeout(async () => {
        await fetchHealthData();
        setRefreshing(false);
      }, 2000);
    } catch (error) {
      toast.error('Failed to refresh analytics');
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'demo':
        return <Activity className="h-4 w-4 text-blue-500" />;
      case 'stale':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'not_connected':
        return <WifiOff className="h-4 w-4 text-slate-400" />;
      default:
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      healthy: { label: 'Live', className: 'bg-green-100 text-green-700 border-green-200' },
      demo: { label: 'Demo', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      stale: { label: 'Stale', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      not_connected: { label: 'Not Connected', className: 'bg-slate-100 text-slate-500 border-slate-200' }
    };
    const config = configs[status] || configs.not_connected;
    return (
      <Badge variant="outline" className={`text-xs ${config.className}`}>
        {config.label}
      </Badge>
    );
  };

  const getOverallStatusColor = (status) => {
    switch (status) {
      case 'all_healthy':
        return 'text-green-600';
      case 'demo_only':
        return 'text-blue-600';
      case 'needs_attention':
        return 'text-amber-600';
      case 'no_connections':
        return 'text-slate-500';
      default:
        return 'text-slate-600';
    }
  };

  const getOverallStatusText = (status) => {
    switch (status) {
      case 'all_healthy':
        return 'All platforms healthy';
      case 'demo_only':
        return 'Using demo data';
      case 'needs_attention':
        return 'Some platforms need attention';
      case 'no_connections':
        return 'No platforms connected';
      case 'mixed':
        return 'Mixed status';
      default:
        return 'Unknown status';
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse" data-testid="analytics-health-widget">
        <CardHeader className="pb-2">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded w-full"></div>
            <div className="h-4 bg-slate-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!healthData) {
    return null;
  }

  const { summary, platforms } = healthData;

  // Get connected platforms only for display
  const connectedPlatforms = Object.entries(platforms)
    .filter(([_, data]) => data.connected)
    .sort((a, b) => {
      // Sort by status: healthy first, then demo, then stale
      const order = { healthy: 0, demo: 1, stale: 2 };
      return (order[a[1].health_status] || 3) - (order[b[1].health_status] || 3);
    });

  if (compact) {
    // Compact version for dashboard sidebar
    return (
      <Card className="border-slate-200" data-testid="analytics-health-widget-compact">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-emerald-500" />
              Analytics Health
            </span>
            <span className={`text-lg font-bold ${summary.overall_score >= 75 ? 'text-green-600' : summary.overall_score >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
              {summary.overall_score}%
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <Progress 
            value={summary.overall_score} 
            className="h-2 mb-2"
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{summary.connected} of {summary.total_platforms} connected</span>
            <span className={getOverallStatusColor(summary.overall_status)}>
              {summary.healthy} live
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full version
  return (
    <Card className="border-slate-200" data-testid="analytics-health-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-500" />
            Analytics Health
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8"
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-slate-600">Overall Health Score</span>
              <span className={`text-2xl font-bold ${summary.overall_score >= 75 ? 'text-green-600' : summary.overall_score >= 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                {summary.overall_score}%
              </span>
            </div>
            <Progress 
              value={summary.overall_score} 
              className="h-2"
            />
            <p className={`text-xs mt-1 ${getOverallStatusColor(summary.overall_status)}`}>
              {getOverallStatusText(summary.overall_status)}
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-2 py-2 border-y border-slate-100">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">{summary.healthy}</div>
            <div className="text-xs text-slate-500">Live</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600">{summary.demo}</div>
            <div className="text-xs text-slate-500">Demo</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-amber-600">{summary.stale}</div>
            <div className="text-xs text-slate-500">Stale</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-slate-400">{summary.not_connected}</div>
            <div className="text-xs text-slate-500">Not Set</div>
          </div>
        </div>

        {/* Platform List */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">Connected Platforms</h4>
          {connectedPlatforms.length === 0 ? (
            <p className="text-sm text-slate-500 py-2">
              No platforms connected. Go to Calendar Settings to connect your social accounts.
            </p>
          ) : (
            <div className="space-y-1.5">
              {connectedPlatforms.map(([platformId, data]) => {
                const config = PLATFORM_CONFIG[platformId] || { name: platformId, icon: '📱' };
                return (
                  <div 
                    key={platformId}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`platform-health-${platformId}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{config.icon}</span>
                      <div>
                        <span className="text-sm font-medium text-slate-700">{config.name}</span>
                        {data.posts_count > 0 && (
                          <span className="text-xs text-slate-400 ml-2">
                            {data.posts_count} post{data.posts_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {data.health_status === 'stale' && data.hours_since_update && (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {data.hours_since_update}h ago
                        </span>
                      )}
                      {getStatusIcon(data.health_status)}
                      {getStatusBadge(data.health_status)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 pt-2 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            <span>Live = Real API data</span>
          </div>
          <div className="flex items-center gap-1">
            <Activity className="h-3 w-3 text-blue-500" />
            <span>Demo = Mock data</span>
          </div>
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-amber-500" />
            <span>Stale = Needs refresh</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AnalyticsHealthWidget;
